import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { makeStyles, Grid, Box } from "@material-ui/core";

import { Top } from "./Top";
import { Stream } from "./Stream";
import { Overview } from "./Overview";
import { Header } from "./Header";
import { AssetDetailMap } from "./AssetDetailMap";
import { getDataName } from "./getDataName";
import { api } from "../../../../../services/api";
import { requestDateFormat } from "../../../../../services";
import { COLORS } from "../../../../../theme/colors";
import { DATE_PART_PERIODS } from "../../../../dashboard/constants";
import { ShaDowBox } from "../../../../../components/boxes/ShadowBox";
import { NoDataCard } from "../../../../../components/parts/noDataCard";
import { currencyFormatter, nKilowattFormatter } from "../../../../../services";
import { DoubleBarChart } from "../../../../../components/charts/DoubleBarChart";
import { AssetDetailActivities } from "./AssetDetailActivities";
import { Help } from "../../../../../components/help";
import { getAssetDetailBarChartLegend } from "./getAssetDetailBarChartLegend";

export const helpRows = [
  {
    label: "Technical information & Documents",
  },
  {
    label: "Commercial Terms",
  },
  {
    label: "FAQ",
  },
];

const useStyles = makeStyles((theme) => ({
  box: {
    padding: `${theme.spacing(3)}px 0`,
  },
  chartBox: {
    height: "396px",
  },
}));

export const AssetDetail = ({ id, setIsList }) => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(DATE_PART_PERIODS[0]);
  const [chartRawData, setChartRawData] = useState([]);
  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);
  const [chartData, setChartData] = useState([]);
  const [bars, setBars] = useState([]);
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);
  const TOGGLE_OPTIONS = {
    revenues: {
      option: currencySymbol,
      title: "Power Revenues",
      key: "revenues",
      unit: currencySymbol,
      getTickFormatter: (currencySymbol) => (tick) => currencyFormatter(tick, currencySymbol),
    },
    power: {
      option: "kWh",
      title: "Power Production",
      key: "power",
      unit: "kwh",
      getTickFormatter: () => (tick) => nKilowattFormatter(tick),
    },
  };
  const optionsMap = new Map(
    Object.values(TOGGLE_OPTIONS).map((toggleOption) => [toggleOption.option, { ...toggleOption }])
  );
  const toggleOptions = [...optionsMap.keys()];
  const [toggleFilter, setToggleFilter] = useState(TOGGLE_OPTIONS.revenues.option);

  useEffect(() => {
    const getAssetDetail = () => {
      api.get(`/praas/v1/power-assets/${id}/`).then((resp) => setData(resp.data));
    };

    getAssetDetail();
  }, [id]);

  useEffect(() => {
    const getComparativeProductionData = () => {
      let url = `/praas/v1/power-assets/comparative-production/${id}/`;

      if (period.name === "RANGE" && startDate && endDate) {
        url += `?from_date=${requestDateFormat(startDate)}&to_date=${requestDateFormat(endDate)}`;
      } else {
        url += `?period=${period.filter}`;
      }

      api.get(url).then((resp) => {
        const bars = [];

        resp.data.results.forEach((item, i) => {
          const fill = i ? "#ECA447" : COLORS.purple;
          const dataKey = String(item.period);

          bars.push({ dataKey, fill });
        });

        setBars(bars);
        setChartRawData(resp.data.results);
      });
    };

    getComparativeProductionData();
  }, [id, period]);

  useEffect(() => {
    const data = [];

    chartRawData.forEach((item) => {
      const dataKey = String(item.period);
      const key = optionsMap.get(toggleFilter).key;

      item.values.forEach((itemValue, j) => {
        if (data[j]) {
          const tempData = data[j];
          const finalData = {
            ...tempData,
            [dataKey]: itemValue[key],
            unit: optionsMap.get(toggleFilter).unit,
          };

          data[j] = finalData;
        } else {
          const date = new Date(itemValue.date);

          data.push({
            name: getDataName(date, period),
            [dataKey]: itemValue[key],
            unit: optionsMap.get(toggleFilter).unit,
          });
        }
      });
    });

    setChartData(data);
  }, [chartRawData, toggleFilter]);

  const chartComponent = chartData.length ? (
    <DoubleBarChart
      data={chartData}
      bars={bars}
      horizontal={true}
      vertical={false}
      margin={{
        top: 5,
        right: 30,
        left: 20,
        bottom: 5,
      }}
      tickFormatter={optionsMap.get(toggleFilter).getTickFormatter(currencySymbol)}
    >
      {getAssetDetailBarChartLegend(period)}
    </DoubleBarChart>
  ) : (
    <NoDataCard type="simple" />
  );

  return (
    <div className={classes.box}>
      {data ? (
        <div>
          <Header setIsList={setIsList} data={data} />
          <Grid container style={{ marginTop: 40 }} spacing={3}>
            <Grid container item sm={8} spacing={2} alignContent="flex-start">
              <Grid item xs={12}>
                <ShaDowBox>
                  <Overview
                    accumulatedEmissionSaved={data["emission_saved"]}
                    accumulatedRevenues={data["acc_revenue"]}
                    currentProduction={data["production"]}
                    installedCapacity={data["installed_capacity"]}
                  />
                </ShaDowBox>
              </Grid>
              <Grid item xs={12}>
                <ShaDowBox>
                  <Top
                    period={period}
                    startDate={startDate}
                    endDate={endDate}
                    onPeriodChange={(period, start, end) => {
                      setPeriod(period);
                      setStartDate(start);
                      setEndDate(end);
                    }}
                    bars={bars}
                    setToggleFilter={setToggleFilter}
                    toggleFilter={toggleFilter}
                    toggleOptions={toggleOptions}
                    title={optionsMap.get(toggleFilter).title}
                  />
                  <Box className={classes.chartBox}>{chartComponent}</Box>
                </ShaDowBox>
              </Grid>
            </Grid>
            <Grid item sm={4}>
              <ShaDowBox style={{ padding: 15 }}>
                <AssetDetailMap building={data.building} />
                <Stream status={data.status} />
              </ShaDowBox>
            </Grid>
            <Grid item xs={12}>
              <AssetDetailActivities powerAssetActivities={data["power_assets_activity"]} />
            </Grid>
            <Grid item xs={12}>
              <Help title="More Info" rows={helpRows} />
            </Grid>
          </Grid>
        </div>
      ) : null}
    </div>
  );
};
